import { AfterContentChecked, AfterContentInit, Component, forwardRef, Inject, Input, NgZone, OnDestroy, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { SlideshowHost } from './slideshow-host';

const DEFAULT_DELAY = 5000;
const DEFAULT_FADE = 800;
// Below this a "crossfade" is just a flicker, and a mistyped delay="10" would
// spin the timer.  Anything smaller falls back to the default.
const MINIMUM_DELAY = 100;

// A set of app-slide children cycled with a crossfade.  It shares the geometry
// of app-illustration - the padding-bottom ratio box, cover/backgroundPosition,
// the overlay layer - but drops the lightbox: clicking pauses and resumes
// instead.  Every slide stays in the rendered DOM with its own src and alt, so
// search engines index all of them, not just the visible one.
@Component({
  selector: 'app-slideshow',
  templateUrl: './slideshow.component.html',
  styleUrls: ['./slideshow.component.scss'],
  providers: [
    { provide: SlideshowHost, useExisting: forwardRef(() => SlideshowComponent) }
  ]
})
export class SlideshowComponent implements SlideshowHost, AfterContentInit, AfterContentChecked, OnDestroy {

  // Milliseconds each slide is held.  Arrives as a string from a template
  // (delay="4000"), so it is coerced before use.
  @Input()
  delay:number = DEFAULT_DELAY;

  // Milliseconds the crossfade itself takes.
  @Input()
  fadeDuration:number = DEFAULT_FADE;

  // These arrive as strings from templates (width="1586"), so coerce before use.
  @Input()
  width:number;

  @Input()
  height:number;

  @Input()
  cover:boolean = false;

  // Named after the app-illustration input it mirrors - maps to object-position.
  @Input()
  backgroundPosition:string = 'center';

  // Named after the app-illustration input it mirrors - painted as an overlay
  // layer above the slides.
  @Input()
  backgroundImageOverlayCss?:string;

  // Clicking a running slideshow pauses it, which is also what makes the
  // auto-advancing content stoppable.  Opt out where a click means something
  // else, the way app-illustration takes clickable="false".
  @Input()
  pausable:boolean = true;

  index = 0;

  paused = false;

  // Only ever true in the browser, with more than one slide and motion allowed,
  // so a server-rendered slideshow is simply its first slide.
  cycling = false;

  // True in the browser with more than one slide, whether or not the slideshow
  // is allowed to advance by itself: stepping through by hand is a deliberate
  // act, so the arrows are offered even where reduced motion stopped the timer.
  navigable = false;

  private _slides:object[] = [];
  private _timer:any = null;
  private _contentReady = false;
  private _motionAllowed?:boolean;

  constructor(private _ngZone:NgZone,
              @Inject(PLATFORM_ID) private _platformId:Object) {
  }

  ngAfterContentInit() {
    // Deliberately not ngAfterViewInit: this hook runs before this component's
    // own view bindings are checked, so setting cycling here cannot trip
    // ExpressionChangedAfterItHasBeenChecked.
    this._contentReady = true;
    this._refresh();
  }

  ngAfterContentChecked() {
    // Slides can come and go (an *ngFor of app-slide), and register/unregister
    // during view creation rather than through a query, so re-check the count
    // on every pass.  Same ordering argument as above.
    this._refresh();
  }

  ngOnDestroy() {
    this._stop();
  }

  register(slide:object) {
    // Constructor order is document order, for a static template and for an
    // *ngFor alike.
    this._slides.push(slide);
  }

  unregister(slide:object) {
    const position = this._slides.indexOf(slide);
    if(position < 0) {
      return;
    }
    this._slides.splice(position, 1);
    if(this.index >= this._slides.length) {
      this.index = 0;
    }
  }

  isActive(slide:object):boolean {
    return this._slides[this.index] === slide;
  }

  isFirst(slide:object):boolean {
    return this._slides[0] === slide;
  }

  get interactive():boolean {
    return this.pausable && this.cycling;
  }

  get hasIntrinsicRatio():boolean {
    return !!(this.width && this.height);
  }

  get paddingBottom():string {
    return this.hasIntrinsicRatio ? 100*(+this.height / +this.width)+'%' : '0';
  }

  get explicitHeight():string|null {
    return this.hasIntrinsicRatio ? null : '100%';
  }

  // Read by the slides through inheritance of the custom property, so nothing
  // has to be pushed across the content projection boundary.
  get fadeCss():string {
    const duration = +this.fadeDuration;
    return (isFinite(duration) && duration >= 0 ? duration : DEFAULT_FADE)+'ms';
  }

  get effectiveDelay():number {
    const delay = +this.delay;
    return isFinite(delay) && delay >= MINIMUM_DELAY ? delay : DEFAULT_DELAY;
  }

  // The accessible name of the pause control.  The visible hint is phrased for
  // the pointer that is hovering it; this one is not.
  get label():string {
    return this.paused ? 'Resume slideshow' : 'Pause slideshow';
  }

  get hint():string {
    return this.paused ? 'Click to resume' : 'Click to pause';
  }

  togglePaused = (event?:Event) => {
    if(event) {
      // The hint button sits inside the container, whose own click already
      // toggles - without this the two would cancel out.
      event.stopPropagation();
    }
    if(!this.interactive) {
      return;
    }
    this.paused = !this.paused;
    if(this.paused) {
      this._stop();
    } else {
      this._start();
    }
  }

  previous = (event?:Event) => {
    this._step(-1, event);
  }

  next = (event?:Event) => {
    this._step(1, event);
  }

  private _step(direction:number, event?:Event) {
    if(event) {
      // As above: the arrows are inside the click-to-pause container, and
      // stepping through the slides is not a request to pause.
      event.stopPropagation();
    }
    if(!this.navigable) {
      return;
    }
    const count = this._slides.length;
    this.index = (this.index + direction + count) % count;
    // A slide reached by hand gets a full delay of its own rather than whatever
    // was left of the one it replaced.
    if(this.cycling && !this.paused) {
      this._start();
    }
  }

  private _refresh() {
    if(!this._contentReady) {
      return;
    }
    this.navigable = isPlatformBrowser(this._platformId) && this._slides.length > 1;
    const shouldCycle = this.navigable && this._isMotionAllowed();
    if(shouldCycle === this.cycling) {
      return;
    }
    this.cycling = shouldCycle;
    if(shouldCycle && !this.paused) {
      this._start();
    } else {
      this._stop();
    }
  }

  // Reduced motion stops the slideshow advancing at all rather than only
  // dropping the fade: auto-updating content is the thing being opted out of.
  private _isMotionAllowed():boolean {
    if(this._motionAllowed === undefined) {
      this._motionAllowed = !(typeof window !== 'undefined'
        && window.matchMedia
        && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
    }
    return this._motionAllowed;
  }

  private _start() {
    this._stop();
    const delay = this.effectiveDelay;
    // Outside the zone, so an idle slideshow does not run change detection on
    // every tick; back inside it only when a slide actually changes.
    this._ngZone.runOutsideAngular(() => {
      this._timer = setInterval(() => {
        this._ngZone.run(() => {
          this.index = this._slides.length ? (this.index + 1) % this._slides.length : 0;
        });
      }, delay);
    });
  }

  private _stop() {
    if(this._timer !== null) {
      clearInterval(this._timer);
      this._timer = null;
    }
  }

}
